import { AUTH_ERROR_TYPES } from '@constants'
import { ERROR_SUMMARY_FRAGMENTS, OKTA_STATUS, RECOVERY_ERROR_CODES } from '@shared/constants/account'

/** Okta-shaped error from registration MFA verify (HTTP error body or thrown Error with assign). */
export type RegistrationMfaApiError = {
  errorCode?: string
  errorSummary?: string
  status?: string
  message?: string
  statusCode?: number
  errorCauses?: Array<{ errorSummary?: string }>
}

export function isOktaInvalidTokenError(apiError: RegistrationMfaApiError | null | undefined): boolean {
  if (!apiError) return false
  const summary = typeof apiError.errorSummary === 'string' ? apiError.errorSummary.toLowerCase() : ''
  const invalidTokenFragment = ERROR_SUMMARY_FRAGMENTS.INVALID_TOKEN.toLowerCase()
  return apiError.errorCode === RECOVERY_ERROR_CODES.OKTA_INVALID_TOKEN || summary.includes(invalidTokenFragment)
}

export function isOktaMfaLockoutError(apiError: RegistrationMfaApiError | null | undefined): boolean {
  if (!apiError) return false
  const summary = typeof apiError.errorSummary === 'string' ? apiError.errorSummary.toLowerCase() : ''
  const message = typeof apiError.message === 'string' ? apiError.message.toLowerCase() : ''
  const causesLocked = Array.isArray(apiError.errorCauses) && apiError.errorCauses.some((c) => (c.errorSummary || '').toLowerCase().includes('locked'))

  return (
    apiError.status === OKTA_STATUS.LOCKED_OUT ||
    apiError.errorCode === RECOVERY_ERROR_CODES.OKTA_ACCOUNT_LOCKED ||
    apiError.errorCode === RECOVERY_ERROR_CODES.OKTA_USER_LOCKED ||
    summary.includes(ERROR_SUMMARY_FRAGMENTS.ACCOUNT_LOCKED.toLowerCase()) ||
    summary.includes(ERROR_SUMMARY_FRAGMENTS.USER_LOCKED.toLowerCase()) ||
    message.includes('locked') ||
    causesLocked ||
    (Boolean(apiError.statusCode && [403, 423].includes(apiError.statusCode)) && summary.includes('locked'))
  )
}

/** True when Okta indicates the MFA passcode/OTP timed out (distinct from wrong code when summary mentions expiry). */
export function isOktaMfaPasscodeExpiredError(apiError: RegistrationMfaApiError | null | undefined): boolean {
  if (!apiError) return false
  if (isOktaMfaLockoutError(apiError)) return false
  if (apiError.errorCode === RECOVERY_ERROR_CODES.OKTA_INVALID_TOKEN || apiError.errorCode === RECOVERY_ERROR_CODES.OKTA_EXPIRED_TOKEN) {
    return false
  }
  const causesExpired =
    Array.isArray(apiError.errorCauses) && apiError.errorCauses.some((c) => typeof c.errorSummary === 'string' && c.errorSummary.toLowerCase().includes('expir'))
  if (causesExpired) return true

  const summary = typeof apiError.errorSummary === 'string' ? apiError.errorSummary.toLowerCase() : ''
  const message = typeof apiError.message === 'string' ? apiError.message.toLowerCase() : ''
  const haystack = `${summary} ${message}`.trim()
  if (!haystack.includes('expir')) return false
  if (haystack.includes('session') || haystack.includes('invalid token') || haystack.includes('state token')) return false
  return (
    haystack.includes('passcode') || haystack.includes('verification code') || haystack.includes('code you entered') || (haystack.includes('code') && haystack.includes('expired'))
  )
}

export function isOktaInvalidPasscodeError(apiError: RegistrationMfaApiError | null | undefined): boolean {
  if (!apiError) return false
  if (isOktaMfaPasscodeExpiredError(apiError)) return false
  const summary = typeof apiError.errorSummary === 'string' ? apiError.errorSummary.toLowerCase() : ''
  const invalidPasscodeFragment = ERROR_SUMMARY_FRAGMENTS.INVALID_PASSCODE.toLowerCase()
  return apiError.errorCode === RECOVERY_ERROR_CODES.OKTA_INVALID_PASSCODE || summary.includes(invalidPasscodeFragment)
}

/** Rate-limited authn/MFA requests (Okta E0000118 or HTTP 429 / message). */
export function isOktaMfaRateLimitError(apiError: RegistrationMfaApiError | null | undefined): boolean {
  if (!apiError) return false
  if (apiError.errorCode === RECOVERY_ERROR_CODES.OKTA_TOO_MANY_REQUESTS) return true
  if (apiError.statusCode === 429) return true
  const summary = typeof apiError.errorSummary === 'string' ? apiError.errorSummary.toLowerCase() : ''
  const message = typeof apiError.message === 'string' ? apiError.message.toLowerCase() : ''
  const haystack = `${summary} ${message}`
  return haystack.includes('rate limit') || haystack.includes('too many requests')
}

export function isNormalizedOktaAccountLockedMessage(apiError: RegistrationMfaApiError | null | undefined): boolean {
  return apiError?.message === AUTH_ERROR_TYPES.OKTA_ACCOUNT_LOCKED
}
